import { useState } from 'react';
import { ChevronLeft, MapPin, Plus, Trash2, Edit2, Loader2 } from 'lucide-react';
import { useAddresses } from '../../hooks/useAPI';
import { useAuthStore } from '../../store/authStore';
import supabase from '../../lib/supabase';

interface SavedAddressesScreenProps {
  onNavigate: (screen: any) => void;
}

export default function SavedAddressesScreen({ onNavigate }: SavedAddressesScreenProps) {
  const { user } = useAuthStore();
  const { data: addresses, isLoading } = useAddresses();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    label: '',
    full_address: '',
    location_notes: '',
    court_name: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAddNew = () => {
    setFormData({
      label: '',
      full_address: '',
      location_notes: '',
      court_name: '',
    });
    setEditingId(null);
    setShowAddForm(true);
  };

  const handleEdit = (address: any) => {
    setFormData({
      label: address.label || '',
      full_address: address.full_address || '',
      location_notes: address.location_notes || '',
      court_name: address.court_name || '',
    });
    setEditingId(address.id);
    setShowAddForm(true);
  };

  const handleSave = async () => {
    if (!formData.full_address.trim()) {
      setError('Address is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingId) {
        // Update existing
        const { error: updateError } = await supabase
          .from('customer_addresses')
          .update({
            label: formData.label,
            full_address: formData.full_address,
            location_notes: formData.location_notes,
            court_name: formData.court_name,
          })
          .eq('id', editingId);

        if (updateError) throw updateError;
      } else {
        // Insert new
        const { error: insertError } = await supabase
          .from('customer_addresses')
          .insert({
            user_id: user?.id,
            label: formData.label,
            full_address: formData.full_address,
            location_notes: formData.location_notes,
            court_name: formData.court_name,
            is_default: (addresses?.length || 0) === 0,
          });

        if (insertError) throw insertError;
      }

      setShowAddForm(false);
      window.location.reload(); // Refresh to show new data
    } catch (err: any) {
      setError(err.message || 'Failed to save address');
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this address?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('customer_addresses')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      window.location.reload();
    } catch (err: any) {
      alert('Failed to delete address: ' + err.message);
    }
  };

  if (showAddForm) {
    return (
      <div className="w-full min-h-screen bg-background pb-24">
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddForm(false)}
              className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg">{editingId ? 'Edit' : 'Add'} Address</h1>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Label (e.g., Home, Work)</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="Home"
              className="w-full h-12 px-4 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Full Address *</label>
            <textarea
              value={formData.full_address}
              onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
              placeholder="123 Main Street, Dubai Marina, Dubai"
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Court Name</label>
            <input
              type="text"
              value={formData.court_name}
              onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
              placeholder="Marina Tennis Courts"
              className="w-full h-12 px-4 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Location Notes</label>
            <textarea
              value={formData.location_notes}
              onChange={(e) => setFormData({ ...formData, location_notes: e.target.value })}
              placeholder="Building 5, Court 3. Call security at gate."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none resize-none"
            />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Address'
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('profile')}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Saved Addresses</h1>
            <p className="text-xs text-muted-foreground">Manage delivery locations</p>
          </div>
          <button
            onClick={handleAddNew}
            className="w-10 h-10 rounded-full bg-[#1A6B4F] text-white hover:bg-[#14563F] transition-colors flex items-center justify-center"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="px-6 py-6 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-secondary rounded-2xl p-4 h-32 animate-pulse" />
            ))}
          </div>
        ) : addresses && addresses.length > 0 ? (
          addresses.map((address: any) => (
            <div key={address.id} className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#1A6B4F]" />
                </div>
                <div className="flex-1 min-w-0">
                  {address.label && (
                    <div className="text-sm font-medium mb-1">{address.label}</div>
                  )}
                  <div className="text-sm text-muted-foreground mb-2">
                    {address.full_address}
                  </div>
                  {address.court_name && (
                    <div className="text-xs text-muted-foreground">
                      Court: {address.court_name}
                    </div>
                  )}
                  {address.is_default && (
                    <div className="inline-block mt-2 px-2 py-0.5 bg-[#D4F04A]/20 text-[#1A6B4F] text-xs rounded-full">
                      Default
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(address)}
                    className="w-8 h-8 rounded-lg hover:bg-secondary transition-colors flex items-center justify-center"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(address.id)}
                    className="w-8 h-8 rounded-lg hover:bg-destructive/10 transition-colors flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
              {address.location_notes && (
                <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                  {address.location_notes}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📍</div>
            <h3 className="text-lg mb-2">No saved addresses</h3>
            <p className="text-muted-foreground mb-6">
              Add your delivery locations for faster checkout
            </p>
            <button
              onClick={handleAddNew}
              className="h-12 px-6 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors"
            >
              Add Address
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
